import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TOPBAR_POPOVER_CLOSE, TOPBAR_POPOVER_DATA } from '../topbar-popover.service';

const ICONS: Record<string, string> = {
  inbox: `<polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
          <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>`,
  notification: `<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                 <path d="M13.73 21a2 2 0 0 1-3.46 0"/>`,
  subscription: `<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                 <polyline points="22,6 12,13 2,6"/>`,
};

@Component({
  selector: 'app-placeholder-dropdown',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- Header -->
    <div class="pd-head">
      <span class="pd-title">
        <svg class="pd-title-ico" width="13" height="13" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             aria-hidden="true" [innerHTML]="iconPaths"></svg>
        {{ title }}
      </span>
      <button class="pd-close" type="button" (click)="close()" aria-label="Close">
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
          <path d="M1 1l7 7M8 1L1 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    </div>

    <!-- Body -->
    <div class="pd-body">
      <div class="pd-icon-wrap">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
             aria-hidden="true" [innerHTML]="iconPaths"></svg>
      </div>
      <p class="pd-msg">{{ subtext }}</p>
      <span class="pd-badge">Coming soon</span>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 300px;
      background: #fff;
      border-radius: 12px;
      border: 1px solid #D5DDE8;
      box-shadow: 0 4px 6px rgba(13,42,69,0.04), 0 12px 40px rgba(13,42,69,0.13);
      overflow: hidden;
      font-family: inherit;
    }

    /* ── Header ── */
    .pd-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 11px 14px;
      background: linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%);
      border-bottom: 1px solid #E2E8F0;
    }
    .pd-title {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 12px;
      font-weight: 700;
      color: var(--navy);
    }
    .pd-title-ico { opacity: 0.65; flex-shrink: 0; }
    .pd-close {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      border: none;
      background: rgba(13,42,69,0.07);
      color: var(--navy);
      cursor: pointer;
      display: grid;
      place-items: center;
      padding: 0;
      flex-shrink: 0;
      transition: background 0.12s, color 0.12s;
      &:hover { background: var(--neg, #D04A4A); color: #fff; }
    }

    /* ── Body ── */
    .pd-body {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 28px 20px 26px;
      gap: 8px;
      text-align: center;
    }
    .pd-icon-wrap {
      width: 52px;
      height: 52px;
      border-radius: 14px;
      background: linear-gradient(135deg, #F1F5F9 0%, #E8EEF5 100%);
      border: 1px solid #E2E8F0;
      display: grid;
      place-items: center;
      color: #7A94AE;
      margin-bottom: 4px;
    }
    .pd-msg {
      font-size: 12px;
      font-weight: 500;
      color: var(--text2, #3C566C);
      margin: 0;
      line-height: 1.5;
      max-width: 220px;
    }
    .pd-badge {
      display: inline-flex;
      align-items: center;
      height: 20px;
      padding: 0 8px;
      border-radius: 10px;
      background: rgba(13,42,69,0.06);
      border: 1px solid rgba(13,42,69,0.1);
      font-size: 10px;
      font-weight: 600;
      color: var(--muted);
      letter-spacing: 0.03em;
      text-transform: uppercase;
      margin-top: 2px;
    }
  `],
})
export class PlaceholderDropdownComponent {
  private readonly closeFn = inject(TOPBAR_POPOVER_CLOSE);
  private readonly data = inject<{ title: string; subtext: string }>(TOPBAR_POPOVER_DATA);

  protected get title(): string { return this.data.title; }
  protected get subtext(): string { return this.data.subtext; }

  protected get iconPaths(): string {
    const key = this.title.toLowerCase();
    if (key.includes('inbox')) return ICONS['inbox'];
    if (key.includes('notif')) return ICONS['notification'];
    return ICONS['subscription'];
  }

  protected close(): void { this.closeFn(); }
}
